import { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, CheckCircle, FileText } from 'lucide-react';

interface ApiKey {
  id: number;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  is_active: number;
}

export function ApiKeyManagement() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newlyCreated, setNewlyCreated] = useState<{ name: string; apiKey: string; keyPrefix: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const endpointUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/external/supply-requests`;

  const loadKeys = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/api-keys`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await response.json();
      setKeys(data.data || []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      setCreating(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ name: newKeyName })
      });
      const data = await response.json();
      if (data.success) {
        setNewlyCreated({
          name: data.data.name,
          apiKey: data.data.apiKey,
          keyPrefix: data.data.keyPrefix
        });
        loadKeys();
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Bu API anahtarini silmek istediginizden emin misiniz?')) return;
    try {
      await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/api-keys/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      loadKeys();
    } catch (error) {
      console.error('Failed to delete API key:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">API Anahtarlari</h1>
          <p className="text-sm text-gray-600 mt-1">Dis sistem entegrasyonu icin API anahtarlarini yonet</p>
        </div>
        <button
          onClick={() => { setShowModal(true); setNewlyCreated(null); setNewKeyName(''); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Anahtar
        </button>
      </div>

      <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-700 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">Partner tedarik API endpointi</p>
            <p className="text-sm text-blue-800 mt-1">Karsi firma Bearer API key ile asagidaki endpoint'e POST atarak Tedarik Yonetimi tablosuna talep gonderebilir.</p>
            <code className="mt-2 block rounded-lg bg-white px-3 py-2 text-xs text-blue-900 border border-blue-100 font-mono overflow-x-auto">{endpointUrl}</code>
            <p className="text-xs text-blue-700 mt-2">Dokuman dosyasi: `docs/external-supply-api.md`</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Anahtar Adi</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">On Ek</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Olusturma</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Son Kullanim</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Islemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">Yukleniyor...</td>
              </tr>
            ) : keys.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">Henuz API anahtari yok</td>
              </tr>
            ) : (
              keys.map((key) => (
                <tr key={key.id}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Key className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{key.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600 font-mono">{key.key_prefix}...</td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(key.created_at).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString('tr-TR') : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      key.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {key.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleDelete(key.id)}
                      className="text-red-600 hover:text-red-800 p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold">
                {newlyCreated ? 'API Anahtari Olusturuldu' : 'Yeni API Anahtari'}
              </h2>
            </div>
            <div className="p-6">
              {newlyCreated ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-900">{newlyCreated.name} anahtari olusturuldu</p>
                      <p className="text-sm text-green-700 mt-1">Bu anahtari guvenli bir yerde saklayin. Tekrar gosterilmeyecektir.</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Anahtari</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newlyCreated.apiKey}
                        readOnly
                        className="flex-1 px-3 py-2 border rounded-lg bg-gray-50 font-mono text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(newlyCreated.apiKey)}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-yellow-800">Kullanim Ornegi:</p>
                    <pre className="text-xs text-yellow-700 block mt-1 font-mono overflow-x-auto">{`curl -X POST ${endpointUrl} \\
  -H "Authorization: Bearer ${newlyCreated.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"loadingCustomerName":"...","loadingCity":"...","deliveryCustomerName":"...","deliveryCity":"...","loadingDate":"2026-03-27T08:30:00+03:00"}'`}</pre>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Anahtar Adi</label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="Ornegin: Depo Sistemi Entegrasyonu"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowModal(false); setNewlyCreated(null); setNewKeyName(''); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {newlyCreated ? 'Kapat' : 'Iptal'}
              </button>
              {!newlyCreated && (
                <button
                  onClick={handleCreate}
                  disabled={!newKeyName.trim() || creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {creating ? 'Olusturuluyor...' : 'Olustur'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
